import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { auditTime, catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { LocationsSelectors } from './location.selector';

import { WlcmQueryParams } from '@wlcm/common';
import { LocationsState } from './location.state';
import { EffectsBase } from '@store/models';

import {
  LoadLocationsPaginatedActions,
  LoadLocationsPaginatedMoreActions,
  AddLocation,
  PatchLocationAction,
  DeleteLocation,
} from './location.actions';
import { LocationApi } from '@core/services/api/location.api';
import { LocationPayload } from '@core/models/entities/location.models';

@Injectable()
export class LocationsEffects extends EffectsBase<LocationsState> {
  loadPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadLocationsPaginatedActions.init),
      this.mapWlcmQueryParams(LocationsSelectors.queryParams),
      switchMap((params: WlcmQueryParams) =>
        this.api.getPaginated(params).pipe(auditTime(300)).pipe(this.handleResponse(LoadLocationsPaginatedActions))
      )
    )
  );

  loadPaginatedMore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadLocationsPaginatedMoreActions.init),
      this.pullWlcmQueryParams(LocationsSelectors.queryParams),
      exhaustMap((params: WlcmQueryParams) =>
        this.api.getPaginated(params).pipe(this.handleResponse(LoadLocationsPaginatedMoreActions))
      )
    )
  );

  Add$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddLocation.init),
      switchMap(({ payload: { data } }) => this.api.add(data).pipe(this.handleResponse(AddLocation)))
    )
  );

  patch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PatchLocationAction.init),
      switchMap(({ payload: { id, data } }) =>
        this.api.patch(id!, data as LocationPayload).pipe(this.handleResponse(PatchLocationAction))
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteLocation.init),
      switchMap(({ payload: { id } }) =>
        this.api.delete({ id }).pipe(
          map(() =>
            DeleteLocation.success({
              response: {
                id: id,
              },
            })
          ),
          catchError((error) => of(DeleteLocation.failure({ error })))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private api: LocationApi,
    public override store: Store
  ) {
    super(store);
  }
}
